import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { DEFAULT_AUTO_CLOSE_DURATION_MS } from "components/designV2/autoCloseCountdown/autoCloseCountdown";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import type { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import { TimeReportForStamping } from "model/Company";
import { StampClockTeam } from "model/OrganizationalStructure";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlertStore } from "stores/alertStore/alertStore";
import styled from "styled-components";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { Text } from "./clockStamp.styled";

type Props = {
  setShowCreateAutoGeneratedGigModal: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setKeyShouldBeDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  showCreateAutoGeneratedGigModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setTimeReport: React.Dispatch<
    React.SetStateAction<TimeReportForStamping | undefined>
  >;
  setIsAutoGeneratedGig: React.Dispatch<React.SetStateAction<boolean>>;
  clearInputs: any;
  teams: StampClockTeam[];
  workerIdentifier: string | undefined;
};

type CreateAutoGeneratedGigResponse =
  | TimeReportForStamping
  | { active: TimeReportForStamping };

const resolveCreatedTimeReport = (
  response: CreateAutoGeneratedGigResponse,
): TimeReportForStamping => {
  if ("active" in response) {
    return response.active;
  }

  return response;
};

const extractTimeFromISO = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  } catch {
    return "";
  }
};

const parseHHMMToMinutes = (time: string): number => {
  const [hStr, mStr] = time.split(":");
  const hours = Number(hStr);
  const minutes = Number(mStr);
  if (isNaN(hours) || isNaN(minutes)) return 0;
  return hours * 60 + minutes;
};

const getPlannedMinutesFromCompanySettings = (
  startTime: string,
  endTime: string,
): number => {
  const startMinutes = parseHHMMToMinutes(startTime);
  const endMinutes = parseHHMMToMinutes(endTime);

  let diff = endMinutes - startMinutes;
  if (diff < 0) {
    diff += 24 * 60;
  }

  return diff;
};

const addMinutesToIso = (iso: string, minutes: number): string => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  date.setMinutes(date.getMinutes() + minutes);
  return date.toISOString();
};

const nowISO = () => {
  const now = new Date();
  return now.toISOString();
};

export const ClockStampCreateAutoGeneratedGigModal: React.FC<Props> = ({
  setShowCreateAutoGeneratedGigModal,
  setKeyShouldBeDisabled,
  showCreateAutoGeneratedGigModal,
  setShowModal,
  setTimeReport,
  setIsAutoGeneratedGig,
  clearInputs,
  teams,
  workerIdentifier,
}) => {
  const { t } = useTranslation();
  const [companyState, companyActions] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();

  const defaultStartTime =
    companyState.companySettings?.find(
      (s: any) => s.key === "CompanyGigShiftStartTime",
    )?.value ?? "08:00";

  const defaultEndTime =
    companyState.companySettings?.find(
      (s: any) => s.key === "CompanyGigShiftEndTime",
    )?.value ?? "17:00";

  const defaultBreakMinutesString =
    companyState.companySettings?.find((s: any) => s.key === "GigBreakTime")
      ?.value ?? "0";

  const plannedMinutes = useMemo(
    () =>
      getPlannedMinutesFromCompanySettings(defaultStartTime, defaultEndTime),
    [defaultStartTime, defaultEndTime],
  );

  const initialNow = useMemo(() => nowISO(), []);

  const [startTime] = useState<string>(() => initialNow);
  const [endTime] = useState<string>(() =>
    addMinutesToIso(initialNow, plannedMinutes),
  );

  const validateStartTime = (isoString: string): string | null => {
    const timeString = extractTimeFromISO(isoString);
    if (!timeString) return "Invalid time format";

    const now = new Date();
    const [hours, minutes] = timeString.split(":").map(Number);

    const selectedTime = new Date();
    selectedTime.setHours(hours, minutes, 0, 0);

    const oneHourBefore = new Date(now.getTime() - 60 * 60 * 1000);
    const oneHourAfter = new Date(now.getTime() + 60 * 60 * 1000);

    if (selectedTime < oneHourBefore || selectedTime > oneHourAfter) {
      return t("StampClock.StartTimeMustBeWithinOneHour");
    }

    return null;
  };

  const validateEndTime = (
    startIsoString: string,
    endIsoString: string,
  ): string | null => {
    const startTimeString = extractTimeFromISO(startIsoString);
    const endTimeString = extractTimeFromISO(endIsoString);

    if (!startTimeString || !endTimeString) return "Invalid time format";

    const [startHours, startMinutes] = startTimeString.split(":").map(Number);
    const [endHours, endMinutes] = endTimeString.split(":").map(Number);

    const startDate = new Date();
    startDate.setHours(startHours, startMinutes, 0, 0);

    const endDate = new Date();
    endDate.setHours(endHours, endMinutes, 0, 0);

    if (endDate <= startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }

    const timeDifferenceHours =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);

    if (timeDifferenceHours > 14) {
      return t("StampClock.EndTimeMustBeWithin14Hours");
    }

    if (timeDifferenceHours <= 0) {
      return t("StampClock.EndTimeMustBeAfterStartTime");
    }

    return null;
  };

  const startTimeError = validateStartTime(startTime);
  const endTimeError = validateEndTime(startTime, endTime);
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const handleCloseModal = () => {
    setShowCreateAutoGeneratedGigModal(false);
    setKeyShouldBeDisabled(false);
    clearInputs();
  };

  const teamOptions: SearchableDropdownOption[] = useMemo(() => {
    return (
      teams?.map((team) => ({
        value: team.workGroupId.toString(),
        label: team.workGroupName.toString(),
      })) || []
    );
  }, [teams]);

  const hasMultipleTeams = teamOptions.length > 1;

  const [chosenTeam, setChosenTeam] = useState<
    SearchableDropdownOption | undefined
  >(teamOptions.length === 1 ? teamOptions[0] : undefined);

  useEffect(() => {
    setChosenTeam((previous) => {
      if (teamOptions.length === 1) {
        return teamOptions[0];
      }

      if (!previous) {
        return undefined;
      }

      const stillExists = teamOptions.some(
        (option) => option.value === previous.value,
      );
      return stillExists ? previous : undefined;
    });
  }, [teamOptions]);

  const handleAutoGenerateSuccess = (report: TimeReportForStamping): void => {
    setTimeReport(report);
    setIsAutoGeneratedGig(true);
    clearInputs();
    setShowCreateAutoGeneratedGigModal(false);
    setShowModal(true);
    setIsLoading(false);
    alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "",
        title: t("StampClock.Created"),
        message: t("StampClock.CreatedAndClockedIn"),
        autoCloseMs: DEFAULT_AUTO_CLOSE_DURATION_MS,
      },
    });
  };

  const autoGenerateGig = () => {
    const data = {
      WorkGroupId: Number(chosenTeam?.value),
      StartTime: startTime,
      EndTime: endTime,
      breakTime: Number(defaultBreakMinutesString),
    };

    if (companyState.company?.id && workerIdentifier) {
      setIsLoading(true);
      companyActions
        .createAutoGeneratedGig(companyState.company.id, workerIdentifier, data)
        .then((res: CreateAutoGeneratedGigResponse) => {
          const report = resolveCreatedTimeReport(res);
          handleAutoGenerateSuccess(report);
        })
        .catch((err: any) => {
          const msg = err?.response?.data?.errorMessage;

          if (msg === "Overlapping with existing gig") {
            setError(t("StampClock.OverlappingWithExistingGig"));
          } else {
            alertDispatch({
              type: "SHOW_ALERT",
              payload: {
                icon: "alert",
                title: t("Alerts.OhNo"),
                message: t("Alerts.SomethingWentWrong"),
              },
            });
          }
          setIsLoading(false);
          console.error("Error when trying to auto generate shift", err);
        });
    }
  };

  const validateData = () => {
    if (
      startTime &&
      endTime &&
      chosenTeam &&
      !startTimeError &&
      !endTimeError
    ) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    setError(undefined);
  }, [startTime, endTime]);

  return (
    <>
      <Modal
        label={t("StampClock.NoScheduledShiftTitle")}
        size="compact"
        showCloseButton={false}
        autoFocusContent={false}
        isOpen={showCreateAutoGeneratedGigModal}
        onClose={handleCloseModal}
        allowContentOverflow
        footer={
          <ActionsFooter>
            <SecondaryButton
              label={t("StampClock.Cancel")}
              onClick={handleCloseModal}
              disabled={isLoading}
            />
            <PrimaryButton
              label={t("StampClock.CheckIn")}
              onClick={autoGenerateGig}
              disabled={!validateData() || isLoading}
              isLoading={isLoading}
            />
          </ActionsFooter>
        }
      >
        <ModalContent>
          <Text
            fontFamily={FontFamily.MontserratRegular}
            fontSize={FontSize.Standard}
            color={Color.MidnightBlue}
          >
            {t("StampClock.NoScheduledShiftInfo")}
          </Text>
          <Text
            fontFamily={FontFamily.MontserratRegular}
            fontSize={FontSize.Standard}
            color={Color.MidnightBlue}
          >
            {hasMultipleTeams
              ? t("StampClock.AutoGenerateGigInfo")
              : t("StampClock.AutoGenerateGigInfoNoTeam")}
          </Text>
          {error && (
            <Text
              color={Color.LighterDestructive}
              fontSize={FontSize.Small}
              fontFamily={FontFamily.MontserratRegular}
            >
              {error}
            </Text>
          )}
          {hasMultipleTeams && (
            <FormRow $withTopMargin $withBottomPadding>
              <DropdownCell>
                <SearchableDropdown
                  label={t("StampClock.Workgroup")}
                  options={teamOptions}
                  value={chosenTeam?.value}
                  onClear={() => setChosenTeam(undefined)}
                  onChange={(value) => {
                    const option = teamOptions.find((o) => o.value === value);
                    setChosenTeam(option);
                  }}
                  placeholder={t("StampClock.ChooseWorkgroup")}
                  fullWidth
                  size="compact"
                />
              </DropdownCell>
            </FormRow>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

const ActionsFooter = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const FormRow = styled.div<{
  $withTopMargin?: boolean;
  $withBottomPadding?: boolean;
}>`
  display: flex;
  flex-direction: row;
  gap: 12px;
  ${(props) => props.$withTopMargin && "margin-top: 12px;"}
  ${(props) => props.$withBottomPadding && "padding-bottom: 16px;"}
`;

const FormCell = styled.div`
  flex: 1;
  min-width: 0;
`;

const DropdownCell = styled(FormCell)`
  position: relative;
  z-index: 5;
`;

const ModalContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
`;
